import React, { useState, useEffect } from 'react';
import { SavedConfigsManager } from './SavedConfigsManager';
import { ConnectionDiagnostic } from './ConnectionDiagnostic';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { apiUrls } from '../config/api-config';

interface ConfigImportProps {
  sessionId: string;
  onConfigUpdate: (config: any) => void;
}

export function ConfigImport({ sessionId, onConfigUpdate }: ConfigImportProps) {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [currentConfig, setCurrentConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Load session configuration on mount
  useEffect(() => {
    loadSessionConfig();
  }, [sessionId]);

  const loadSessionConfig = async () => {
    try {
      const url = apiUrls.sessionConfig(sessionId);
      console.log('🔗 Loading session config from:', url);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const text = await response.text();
      if (!text) {
        throw new Error('Empty response from server');
      }
      
      const result = JSON.parse(text);
      
      if (result.success) {
        setCurrentConfig(result.config);
        onConfigUpdate(result.config);
        setStatus('success');
        setMessage('Session configuration loaded');
      } else {
        setStatus('error');
        setMessage(result.message || 'Failed to load session configuration');
      }
    } catch (error) {
      console.error('Error loading session config:', error);
      setStatus('error');
      
      // Messages d'erreur plus spécifiques
      if (error.message.includes('Failed to fetch') || error.message.includes('ERR_NAME_NOT_RESOLVED')) {
        setMessage('Cannot connect to server. Please check your internet connection or server status.');
      } else {
        setMessage(`Failed to load session configuration: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, type: 'json' | 'csv') => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      let config;

      if (type === 'json') {
        config = JSON.parse(text);
      } else {
        const lines = text.split('\n').filter(line => line.trim());
        config = { triggers: { en: [], fr: [], ar: [] }, categories: { en: [], fr: [], ar: [] } };

        lines.slice(1).forEach(row => {
          const values = row.split(',').map(v => v.trim());
          if (values.length >= 3) {
            const [en, fr, ar] = values;
            if (en) config.triggers.en.push(en);
            if (fr) config.triggers.fr.push(fr);
            if (ar) config.triggers.ar.push(ar);
          }
        });
      }

      const response = await fetch(apiUrls.importConfig(sessionId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config })
      });

      const result = await response.json();
      
      if (result.success) {
        setStatus('success');
        setMessage('Configuration imported successfully!');
        setCurrentConfig(config);
        onConfigUpdate(config);
      } else {
        setStatus('error');
        setMessage(result.message || 'Import failed');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Error processing file');
    }
  };

  const downloadTemplate = (type: 'json' | 'csv') => {
    let content = '';
    let filename = '';
    let mimeType = '';

    if (type === 'json') {
      content = JSON.stringify({
        triggers: {
          en: ["What if...", "Imagine...", "Create..."],
          fr: ["Et si...", "Imaginez...", "Créez..."],
          ar: ["ماذا لو...", "تخيل...", "إبتكر..."]
        },
        categories: {
          en: ["Technology", "Environment", "Education"],
          fr: ["Technologie", "Environnement", "Éducation"],
          ar: ["التكنولوجيا", "البيئة", "التعليم"]
        }
      }, null, 2);
      filename = 'storm-config-template.json';
      mimeType = 'application/json';
    } else {
      content = "en,fr,ar\nWhat if...,Et si...,ماذا لو...\nImagine...,Imaginez...,تخيل...\nCreate...,Créez...,إبتكر...";
      filename = 'storm-config-template.csv';
      mimeType = 'text/csv';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleConfigLoad = async (config: any) => {
    try {
      // Mettre à jour le serveur avec la nouvelle configuration
      const response = await fetch(apiUrls.importConfig(sessionId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config })
      });

      const result = await response.json();
      
      if (result.success) {
        setCurrentConfig(config);
        onConfigUpdate(config);
        setStatus('success');
        setMessage('Configuration loaded successfully!');
      } else {
        setStatus('error');
        setMessage(result.message || 'Failed to load configuration');
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
      setStatus('error');
      setMessage('Error loading configuration');
    }
  };

  if (loading) {
    return (
      <div className="w-full max-w-6xl mx-auto p-4">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <p className="mt-2 text-sm text-gray-600">Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto p-4">
      <Tabs defaultValue="import" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="import">📁 Import Files</TabsTrigger>
          <TabsTrigger value="saved">💾 Saved Configs</TabsTrigger>
          <TabsTrigger value="diagnostic">🔍 Diagnostic</TabsTrigger>
        </TabsList>

        <TabsContent value="import">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold mb-4">Import Configuration</h2>
            
            {status === 'success' && (
              <div className="mb-4 p-3 bg-green-100 text-green-800 rounded">
                {message}
              </div>
            )}

            {status === 'error' && (
              <div className="mb-4 p-3 bg-red-100 text-red-800 rounded">
                {message}
              </div>
            )}

            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-3">
                <h3 className="font-semibold">JSON Import</h3>
                <p className="text-sm text-gray-600">Import complete configuration</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => downloadTemplate('json')}
                    className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
                  >
                    Download Template
                  </button>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".json"
                      className="hidden"
                      onChange={(e) => handleFileUpload(e, 'json')}
                    />
                    <div className="px-3 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600">
                      Import JSON
                    </div>
                  </label>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold">CSV Import</h3>
                <p className="text-sm text-gray-600">Import via CSV file</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => downloadTemplate('csv')}
                    className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
                  >
                    Download Template
                  </button>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={(e) => handleFileUpload(e, 'csv')}
                    />
                    <div className="px-3 py-2 text-sm bg-green-500 text-white rounded hover:bg-green-600">
                      Import CSV
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-4 text-xs text-gray-500">
              <p><strong>JSON:</strong> {`{"triggers":{"en":[],"fr":[],"ar":[]},"categories":{"en":[],"fr":[],"ar":[]}}`}</p>
              <p><strong>CSV:</strong> en,fr,ar (one item per line)</p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="saved">
          <SavedConfigsManager
            sessionId={sessionId}
            currentConfig={currentConfig}
            onLoadConfig={handleConfigLoad}
          />
        </TabsContent>

        <TabsContent value="diagnostic">
          <ConnectionDiagnostic />
        </TabsContent>
      </Tabs>
    </div>
  );
}